import { useEffect, useRef, useState } from "react";

interface UseHideOnScrollOptions {
  offset?: number;
  threshold?: number;
  /** Через сколько мс покоя снова прятать элемент, показанный скроллом вверх. 0 — не прятать */
  idleHideMs?: number;
}

/** Прячет при скролле вниз, показывает при скролле вверх.
 *  Показанный сам прячется, когда прокрутка замерла */
export const useHideOnScroll = ({
  offset = 0,
  threshold = 4,
  idleHideMs = 2500,
}: UseHideOnScrollOptions = {}) => {
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const frame = useRef(0);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    let idleTimer = 0;

    const update = () => {
      frame.current = 0;

      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;

      // Мелкие рывки и баунс Safari игнорируем
      if (Math.abs(delta) < threshold) return;

      lastScrollY.current = currentY;

      const shouldHide = delta > 0 && currentY > offset;
      setIsHidden(shouldHide);

      // Таймер перезаводится на каждом шаге — сработает только после остановки
      window.clearTimeout(idleTimer);
      if (!shouldHide && idleHideMs > 0 && currentY > offset) {
        idleTimer = window.setTimeout(() => setIsHidden(true), idleHideMs);
      }
    };

    const onScroll = () => {
      if (frame.current) return;
      frame.current = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(idleTimer);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [offset, threshold, idleHideMs]);

  return isHidden;
};
